import { create } from 'zustand';

export type SubscriptionType = 'subscription' | 'collection';
export type SplitMode = 'equal' | 'proportional' | 'fixed';
export type WizardCurrency = 'RUB' | 'USD' | 'EUR';
export type BillingPeriod = 'monthly' | 'quarterly' | 'yearly' | 'custom';
export type Privacy = 'everyone' | 'count_only' | 'nobody';
export type DraftStep = 'type' | 'name' | 'price' | 'members' | 'collect' | 'split' | 'preview';

export interface MemberSlot {
  id: string;
  name: string;
  amount: string;
}

export type DeadlineMode = 'same' | 'before' | 'custom';
export type DeadlineDirection = 'before' | 'after';

function defaultBillingDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export interface WizardData {
  type: SubscriptionType;
  name: string;
  icon: string;
  totalPrice: string;
  currency: WizardCurrency;
  period: BillingPeriod;
  customInterval: number;
  customUnit: 'days' | 'weeks' | 'months';
  billingDate: string;          // ISO "YYYY-MM-DD" first charge date
  deadlineMode: DeadlineMode;
  deadlineDays: number;
  deadlineDirection: DeadlineDirection;
  splitMode: SplitMode;
  memberSlots: MemberSlot[];
  privacy: Privacy;
  showHistory: boolean;
  subscriptionId: string | null; // set after draft is created on step 2
}

interface WizardStore extends WizardData {
  step: number;
  direction: 1 | -1;
  next: () => void;
  prev: () => void;
  setStep: (step: number, direction?: 1 | -1) => void;
  update: (patch: Partial<WizardData>) => void;
  hydrate: (data: Partial<WizardData>, step?: number) => void;
  reset: () => void;
}

export const DRAFT_STEPS: DraftStep[] = ['type', 'name', 'price', 'members', 'collect', 'split', 'preview'];

export function stepIndexFromDraftStep(step: string | null | undefined): number {
  const index = DRAFT_STEPS.indexOf((step ?? 'price') as DraftStep);
  return index >= 0 ? index : 2;
}

export function draftStepFromStepIndex(step: number): DraftStep {
  return DRAFT_STEPS[Math.max(0, Math.min(step, DRAFT_STEPS.length - 1))] ?? 'price';
}

const initial: WizardData = {
  type: 'subscription',
  name: '',
  icon: '',
  totalPrice: '',
  currency: 'RUB',
  period: 'monthly',
  customInterval: 1,
  customUnit: 'months',
  billingDate: defaultBillingDate(),
  deadlineMode: 'same',
  deadlineDays: 3,
  deadlineDirection: 'before',
  splitMode: 'equal',
  memberSlots: [],
  privacy: 'count_only',
  showHistory: false,
  subscriptionId: null,
};

export const useWizardStore = create<WizardStore>((set) => ({
  ...initial,
  step: 0,
  direction: 1,
  next: () => set((s) => ({ step: Math.min(s.step + 1, 6), direction: 1 })),
  prev: () => set((s) => ({ step: Math.max(s.step - 1, 0), direction: -1 })),
  setStep: (step, direction = 1) => set({ step: Math.max(0, Math.min(step, 6)), direction }),
  update: (patch) => set((s) => ({ ...s, ...patch })),
  hydrate: (data, step) => set((s) => ({
    ...s,
    ...data,
    ...(typeof step === 'number' ? { step: Math.max(0, Math.min(step, 6)), direction: 1 as const } : {}),
  })),
  reset: () => set({ ...initial, step: 0, direction: 1 }),
}));
